import { useMemo, useRef, useState } from 'react'
import { FileSpreadsheet, Upload, X } from 'lucide-react'
import type {
  Client,
  ImportField,
  ImportMapping,
  OsintImportResult,
  ParsedTable,
} from '../types'
import { mergeOsintRecords } from '../lib/catalog'
import {
  IMPORT_FIELDS,
  autoMapHeaders,
  readClientFile,
  rowsToClients,
} from '../lib/importer'

interface ImportModalProps {
  existingClients: Client[]
  onClose: () => void
  onImport: (clients: Client[], skipped: number) => void
  onCatalogImport: (result: OsintImportResult) => void
}

const emptyMapping = Object.fromEntries(
  IMPORT_FIELDS.map(({ value }) => [value, null]),
) as ImportMapping

export function ImportModal({
  existingClients,
  onClose,
  onImport,
  onCatalogImport,
}: ImportModalProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [table, setTable] = useState<ParsedTable | null>(null)
  const [mapping, setMapping] = useState<ImportMapping>(emptyMapping)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const previewRows = useMemo(() => table?.rows.slice(0, 4) ?? [], [table])
  const catalogSummary = useMemo(() => {
    if (!table?.osintRecords) return null
    return {
      segments: new Set(table.osintRecords.map((record) => record.osint.segment).filter(Boolean)).size,
      withRevenue: table.osintRecords.filter((record) => record.osint.revenue !== null).length,
      withContracts: table.osintRecords.filter(
        (record) => record.osint.governmentContracts === 'yes',
      ).length,
    }
  }, [table])

  async function handleFile(file?: File) {
    if (!file) return
    setLoading(true)
    setError('')
    try {
      const parsed = await readClientFile(file)
      setTable(parsed)
      setMapping(autoMapHeaders(parsed.headers))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось прочитать файл.')
    } finally {
      setLoading(false)
    }
  }

  function updateMapping(field: ImportField, value: string) {
    setMapping((current) => ({
      ...current,
      [field]: value === '' ? null : Number(value),
    }))
  }

  function completeImport() {
    if (!table) return
    setError('')
    try {
      const result = rowsToClients(table, mapping, existingClients)
      if (!result.clients.length) {
        setError('Новых клиентов не найдено. Проверьте выбранные колонки.')
        return
      }
      onImport(result.clients, result.skipped)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось импортировать клиентов.')
    }
  }

  function completeCatalogImport() {
    if (!table?.osintRecords) return
    onCatalogImport(mergeOsintRecords(table.osintRecords, existingClients))
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="modal import-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <span className="eyebrow">Импорт базы</span>
            <h2 id="import-title">Загрузить клиентов</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Закрыть">
            <X size={20} />
          </button>
        </div>

        {!table ? (
          <button
            className="file-drop"
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault()
              void handleFile(event.dataTransfer.files[0])
            }}
          >
            <span className="file-drop-icon">
              <FileSpreadsheet size={28} />
            </span>
            <strong>{loading ? 'Читаю файл…' : 'Выберите Excel или CSV'}</strong>
            <span>Можно перетащить файл сюда. Первая строка должна содержать заголовки.</span>
          </button>
        ) : (
          <>
            <div className="import-file-summary">
              <FileSpreadsheet size={20} />
              <div>
                <strong>{table.filename}</strong>
                <span>{table.rows.length.toLocaleString('ru-RU')} строк для проверки</span>
              </div>
              <button className="text-button" type="button" onClick={() => setTable(null)}>
                Заменить
              </button>
            </div>

            {table.kind === 'osint' && catalogSummary ? (
              <div className="catalog-import-summary">
                <div>
                  <strong>{table.rows.length.toLocaleString('ru-RU')}</strong>
                  <span>карточек OSINT</span>
                </div>
                <div>
                  <strong>{catalogSummary.segments}</strong>
                  <span>сегментов</span>
                </div>
                <div>
                  <strong>{catalogSummary.withRevenue}</strong>
                  <span>с выручкой</span>
                </div>
                <div>
                  <strong>{catalogSummary.withContracts}</strong>
                  <span>с госконтрактами</span>
                </div>
                <p>
                  Совпадения определяются сначала по ИНН, затем по нормализованному
                  названию. История звонков и договорённости сохраняются.
                </p>
              </div>
            ) : (
              <div className="mapping-grid">
                {IMPORT_FIELDS.map((field) => (
                  <label key={field.value}>
                    <span>
                      {field.label}
                      {field.required ? ' *' : ''}
                    </span>
                    <select
                      value={mapping[field.value] ?? ''}
                      onChange={(event) => updateMapping(field.value, event.target.value)}
                    >
                      <option value="">Не импортировать</option>
                      {table.headers.map((header, index) => (
                        <option value={index} key={`${header}-${index}`}>
                          {header}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            )}

            <div className="preview-wrap">
              <div className="section-heading">
                <h3>Предпросмотр</h3>
                <span>первые {previewRows.length} строки</span>
              </div>
              <div className="table-scroll compact-table">
                <table>
                  <thead>
                    <tr>
                      {table.headers.map((header, index) => (
                        <th key={`${header}-${index}`}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {table.headers.map((_, columnIndex) => (
                          <td key={columnIndex}>{String(row[columnIndex] ?? '')}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <input
          ref={inputRef}
          className="sr-only"
          type="file"
          accept=".xlsx,.csv"
          onChange={(event) => void handleFile(event.target.files?.[0])}
        />

        {error ? <p className="form-error">{error}</p> : null}

        <div className="modal-actions">
          <button className="button secondary" type="button" onClick={onClose}>
            Отмена
          </button>
          {table ? (
            <button
              className="button primary"
              type="button"
              onClick={table.kind === 'osint' ? completeCatalogImport : completeImport}
            >
              <Upload size={18} />
              {table.kind === 'osint'
                ? `Применить каталогизацию (${table.rows.length.toLocaleString('ru-RU')})`
                : `Импортировать ${table.rows.length.toLocaleString('ru-RU')}`}
            </button>
          ) : null}
        </div>
      </section>
    </div>
  )
}
